import { ToastType } from '@components/Toast';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { handleToast } from '@utils/utils';

import {
  ICustomerDetails,
  uploadCustomerDetailsDocument,
} from './customerDetails';
import { addNewCustomerDetail } from './services';

const hasValue = (value: unknown) =>
  value !== null && value !== undefined && value !== '';

const getCustomerCreateChangeSources = (
  customer: Partial<ICustomerDetails>,
  files: File[],
): ChangeTrackingSource[] => {
  const sources: ChangeTrackingSource[] = [];

  if (
    [
      customer.name,
      customer.paymentType,
      customer.mailingAddress1,
      customer.mailingAddress2,
      customer.mailingCity,
      customer.mailingState,
      customer.mailingZip,
      customer.physicalAddress1,
      customer.physicalAddress2,
      customer.physicalCity,
      customer.physicalState,
      customer.physicalZip,
    ].some(hasValue) ||
    customer.loadPending === true
  ) {
    sources.push(CHANGE_TRACKING_SOURCES.customerCreate.nameAndAddress);
  }

  if ((customer.contacts?.length ?? 0) > 0) {
    sources.push(CHANGE_TRACKING_SOURCES.customerCreate.contacts);
  }

  if (files.length > 0) {
    sources.push(
      CHANGE_TRACKING_SOURCES.customerCreate.uploadCreditInformation,
    );
  }

  if (
    [
      customer.accountManagerId,
      customer.accountManagerName,
      customer.accountManagerOfficeId,
      customer.salesManagerId,
      customer.salesManagerName,
      customer.salesManagerOfficeId,
    ].some(hasValue) ||
    (customer.groups?.length ?? 0) > 0 ||
    customer.isPublic === false
  ) {
    sources.push(CHANGE_TRACKING_SOURCES.customerCreate.ownership);
  }

  return sources;
};

export const addNewCustomer = async (data: {
  customer: Partial<ICustomerDetails>;
  file: File[];
  cb: (id: string) => void;
}) => {
  const changeTrackingSources = getCustomerCreateChangeSources(
    data.customer,
    data.file,
  );
  const response = await addNewCustomerDetail(
    data.customer,
    changeTrackingSources,
  );
  if (response && response.isSuccess) {
    const newCustomerId = (response.value as ICustomerDetails | null)?.id;
    if (!newCustomerId) {
      handleToast(ToastType.Error, 'Failed to add customer');
      return;
    }

    if (data.file.length) {
      const res = await Promise.all(
        data.file.map((file) =>
          uploadCustomerDetailsDocument(
            String(newCustomerId),
            file,
            'Public',
            CHANGE_TRACKING_SOURCES.customerCreate.uploadCreditInformation,
          ),
        ),
      );
      if (res.every((item) => item?.results)) {
        handleToast(ToastType.Success, 'Document uploaded successfully');
      }
    }
    handleToast(ToastType.Success, 'Customer added successfully');
    data.cb(String(newCustomerId));
    return response;
  }
};
